import defaultMapStyles, { DEFAULT_MAP_STYLE_PLACEHOLDER } from '../ui/KeplerGl/defaultMapStyles'
import { BuilderBasemapStyle, MapStyleType } from '../types'
import { createSelector } from '@reduxjs/toolkit'
import { MapStyle } from 'react-map-gl/maplibre'
import { RootState } from 'app/state/store'
import { BasemapSettings } from 'features/account/types'
import { _applyLayerGroupFilters as applyLayerGroupFilters } from '@carto/api-client'

const EMPTY_ARRAY = []
export const mapStylesWithLayerGroupsSelector = createSelector(
  (state: RootState) => state.builder.mapStyles,
  (mapStyles) =>
    mapStyles.map((style) => {
      const originalDefinition = defaultMapStyles.find((defaultStyle) => defaultStyle.id === style.id)
      return {
        ...style,
        layerGroups: originalDefinition?.layerGroups || EMPTY_ARRAY
      }
    })
)

export const fallbackMapStyleSelector = createSelector(
  (state: RootState) => state.builder.mapStyles,
  (state: RootState) => state.builder.basemapSettings,
  (mapStyles: BuilderBasemapStyle[], basemapSettings: BasemapSettings) => {
    if (basemapSettings?.carto) {
      const cartoCandidate = mapStyles.find((style) => style.type === MapStyleType.MAPLIBRE && !style.disabled)
      if (cartoCandidate) {
        return cartoCandidate
      }
    }

    if (basemapSettings?.custom) {
      const customCandidate = mapStyles.find((style) => style.type === MapStyleType.CUSTOM && !style.disabled)
      if (customCandidate) {
        return customCandidate
      }
    }

    if (basemapSettings?.google) {
      const googleCandidate = mapStyles.find((style) => style.type === MapStyleType.GOOGLE_MAPS && !style.disabled)
      if (googleCandidate) {
        return googleCandidate
      }
    }
    return defaultMapStyles[0]
  }
)

export const defaultMapStyleSelector = fallbackMapStyleSelector

export const basemapConfigSelector = createSelector(
  (state: RootState) => state.builder.basemapConfig,
  defaultMapStyleSelector,
  (basemapConfig, defaultMapStyle) => {
    return {
      ...basemapConfig,
      styleId: basemapConfig.styleId === DEFAULT_MAP_STYLE_PLACEHOLDER ? defaultMapStyle.id : basemapConfig.styleId
    }
  }
)

export const currentBasemapConfigWithStyleSelector = createSelector(
  basemapConfigSelector,
  (state: RootState) => state.builder.mapStyles,
  (basemapConfig, mapStyles) => {
    return { ...basemapConfig, currentMapStyle: mapStyles.find((style) => style.id === basemapConfig.styleId) }
  }
)

export const currentBasemapIsGoogleMapsSelector = createSelector(
  currentBasemapConfigWithStyleSelector,
  (currentBasemapConfigWithStyle) => {
    return currentBasemapConfigWithStyle.currentMapStyle?.type === MapStyleType.GOOGLE_MAPS
  }
)

export const currentMapComponentStyleSelector = createSelector(
  basemapConfigSelector,
  fallbackMapStyleSelector,
  (state: RootState) => state.builder.mapStyles,
  (currentMapStyle, fallbackMapStyle, mapStyles) => {
    let mapStyle = mapStyles.find((style) => style.id === currentMapStyle.styleId)

    if (!mapStyle || mapStyle?.failedToLoad) {
      mapStyle = fallbackMapStyle
    }

    return {
      style:
        mapStyle?.type === MapStyleType.MAPLIBRE && currentMapStyle.visibleLayerGroups
          ? applyLayerGroupFilters(mapStyle.style as MapStyle, currentMapStyle.visibleLayerGroups)
          : mapStyle?.style,
      type: mapStyle?.type,
      failedToLoad: mapStyle?.failedToLoad ?? (mapStyle ? false : true)
    }
  }
)
export default defaultMapStyles
